import React from 'react'

import Loader from 'react-loader-spinner'

import { KeyboardReactInterface } from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'

import FloorButton from 'components/button/FloorButton'
import Icons from 'components/icons/Icons'

import useI18n from 'i18n/useI18n'

import { TagName } from 'map/Map'
import { MapFloor, RoomFilterType } from 'map/types'
import useMap from 'map/useMap'
import * as mapStore from 'map/mapStore'
import { getRoomTypeEnum } from 'map/utils'
import { RouteResult } from 'map/visioweb'

import useReducer from 'store/useReducer'

import styled from 'theme/styled-components'
import useTheme from 'theme/useTheme'

import useWindowDimensions from 'utils/useWindowDimensions'
import Logger from 'utils/Logger'

import TotemKeyboard from './TotemKeyboard'
import TotemPOIDetails from './TotemPOIDetails'
import TotemItinerary from './TotemItinerary'
import TotemBuildingSelector from './TotemBuildingSelector'

const DEFAULT_INACTIVITY_TIMEOUT = 60 // 60 seconds = 1 min
const DEFAULT_PLACE_COLOR = '#20D4E9'

interface Props {
  hashMap: string
  areas: TotemAreaV6[]
  buildingsLocations: TotemBuildingV6[]
  defaultPOI?: string
  inactivityTimeout?: number
}

const TotemMap = ({ hashMap, areas, buildingsLocations, defaultPOI, inactivityTimeout }: Props) => {
  const i18n = useI18n()
  const [theme, currentTheme] = useTheme()
  const dimensions = useWindowDimensions()

  const [selectedTab, setSelectedTab] = React.useState<TagName>()
  const zoomOnPOI = React.useRef<{ poi: TotemAreaV6 | undefined; instant: boolean }>({ poi: undefined, instant: false })
  const [defaultPOIArea, setDefaultPOIArea] = React.useState<TotemAreaV6>()

  const keyboardRef = React.useRef<KeyboardReactInterface | null>(null)

  const searchBarRef = React.useRef<HTMLInputElement>(null)
  const [searchText, setSearchText] = React.useState<string>('')

  const resultsContainerRef = React.useRef<HTMLDivElement>(null)

  const coloredUniquePlaceName = React.useRef<string>(defaultPOI || '')

  const [itineraryInfo, setItineraryInfo] = React.useState<RouteResult>()

  const [selectedFilter, setSelectedFilter] = React.useState<RoomFilterType>(RoomFilterType.ALL)
  const [selectedPOI, setSelectedPOI] = React.useState<TotemAreaV6>()

  const canClearInput = React.useMemo(() => selectedTab === 'search' || searchText !== '', [selectedTab, searchText])

  const [mapRef, , building, floor, , buildings, actions, itinerary] = useMap(
    [],
    [],
    currentTheme === 'accessible',
    hashMap,
    i18n.lang,
    false,
    [],
    (clickedPoiId: string) => onPOIClicked(clickedPoiId, true),
    dimensions.width,
    dimensions.height,
    [],
    defaultPOI,
    defaultPOI,
    false,
    () => setIsLoading(false)
  )

  const currentBuilding = React.useMemo(() => buildings.find((b) => b.id === building), [building, buildings])
  const currentFloor = React.useMemo(() => currentBuilding && currentBuilding.floors.find((f) => f.id === floor), [
    currentBuilding,
    floor,
  ])

  const [isLoading, setIsLoading] = React.useState(true)

  const allFloors = React.useMemo(() => {
    if (currentBuilding) {
      return currentBuilding.floors.sort((a, b) => b.levelIndex - a.levelIndex)
    }
  }, [currentBuilding])

  const allFloorsRefs = React.useMemo(
    () =>
      allFloors &&
      allFloors.reduce((acc, value) => {
        acc[value.id] = React.createRef()
        return acc
      }, {} as { [key: string]: any }),
    [allFloors]
  )

  React.useEffect(() => {
    if (currentFloor && allFloorsRefs) {
      const floorRef = allFloorsRefs[currentFloor.id]
      if (floorRef && floorRef.current) {
        floorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [currentFloor])

  if (!!!hashMap) {
    return <ErrorMessage>{i18n.t('errors.map.noHash')}</ErrorMessage>
  }

  const renderFloorButton = (floor: MapFloor) => (
    <FloorButton
      ref={allFloorsRefs ? allFloorsRefs[floor.id] : undefined}
      key={floor.id}
      shortName={floor.shortName}
      sizeMultiplier={2}
      selected={currentFloor?.id === floor.id}
      onClick={() => {
        setSelectedTab(undefined)
        !!currentBuilding && actions.goTo(currentBuilding.id, floor.id)
      }}
    />
  )

  const zoomToPOI = (uniquePlaceName: string) => {
    actions.zoomTo({ place: uniquePlaceName }).catch(Logger.error)
    coloredUniquePlaceName.current = uniquePlaceName
    actions.setPlaceColor(uniquePlaceName, theme.colors.darkBlue)
  }

  const onPOIClicked = (uniquePlaceName: string, instant = false) => {
    const poi = areas.find((a) => a.uniquePlaceName === uniquePlaceName)
    if (poi) {
      zoomOnPOI.current = { poi, instant }
      setSelectedTab('details')
      setSelectedPOI(poi)
      actions.setPlace(zoomOnPOI.current.poi as AreaV6)
    }
  }

  React.useEffect(() => {
    if (zoomOnPOI.current.poi && actions) {
      actions.setPlaceColor(coloredUniquePlaceName.current, DEFAULT_PLACE_COLOR)
      const zoomOnPOIFloor = allFloors?.find((f) => f.levelIndex === zoomOnPOI.current.poi?.floor)?.id
      const zoomOnPOIBuilding = buildings.find((b) => b.floors.some((f) => f.id === zoomOnPOIFloor))
      const uniquePlaceName = zoomOnPOI.current.poi?.uniquePlaceName
      if (!zoomOnPOI.current.instant && zoomOnPOIBuilding && zoomOnPOIFloor !== currentFloor?.id) {
        actions.goTo(zoomOnPOIBuilding.id, zoomOnPOIFloor)
        setTimeout(() => zoomToPOI(uniquePlaceName), 2000)
      } else {
        zoomToPOI(uniquePlaceName)
      }
      zoomOnPOI.current = { poi: undefined, instant: false }
    }
  }, [zoomOnPOI.current, actions])

  // Inactivity timeout, after the desired time, the map will zoom to the default POI
  const inactivityTimeoutRef = React.useRef<number>()
  React.useEffect(() => {
    window.addEventListener('touchend', (e) => {
      !!inactivityTimeoutRef.current && clearTimeout(inactivityTimeoutRef.current)
      inactivityTimeoutRef.current = setTimeout(() => {
        if (!!defaultPOI) {
          onPOIClicked(defaultPOI)
        }
        inactivityTimeoutRef.current = undefined
      }, (inactivityTimeout || DEFAULT_INACTIVITY_TIMEOUT) * 1000)
    })
    setDefaultPOIArea(areas.find((a) => a.uniquePlaceName === defaultPOI))
  }, [])

  // When the default POI is set, zoom to it after 4 seconds (so the map is fully loaded)
  React.useEffect(() => {
    if (!isLoading && !!defaultPOI) {
      setTimeout(() => onPOIClicked(defaultPOI), 4000)
    }
  }, [isLoading, defaultPOI])

  const poiResults = React.useMemo(() => {
    return areas
      .filter((area) => (searchText ? area.name.toLowerCase().includes(searchText.toLowerCase()) : true))
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((area) =>
        selectedFilter === RoomFilterType.ALL ? true : getRoomTypeEnum(area.placeType) === selectedFilter
      )
  }, [searchText, selectedFilter, areas])

  const itineraryStatus = useReducer(mapStore.store, (s) => s.itineraryStatus)

  const computeItinerary = (uniquePlaceName: string) => {
    setSelectedTab('itinerary')
    itinerary.setFrom(defaultPOIArea as AreaV6)
    itinerary.setTo(areas.find((a) => a.uniquePlaceName === uniquePlaceName) as AreaV6)
    if (defaultPOI && uniquePlaceName) {
      mapStore.actions.setItineraryStatus('loading')
      return itinerary
        .computeRoute(defaultPOI, uniquePlaceName, false, i18n.lang)
        .then((res) => {
          if (!!res) {
            mapStore.actions.setItineraryStatus('ready')
            setItineraryInfo(res)
          } else {
            mapStore.actions.setItineraryStatus('error')
          }
        })
        .catch((err) => {
          Logger.error(err)
          mapStore.actions.setItineraryStatus('error')
        })
    }
    mapStore.actions.setItineraryStatus('notStarted')
    return Promise.resolve()
  }

  const stopItinerary = () => {
    setSelectedTab(undefined)
    mapStore.actions.setItineraryStatus('notStarted')
    itinerary.stopRoute()
  }

  const onBottomRightButtonClick = () => {
    if (isItineraryRunning) {
      stopItinerary()
    } else {
      defaultPOI ? onPOIClicked(defaultPOI, true) : actions.zoomTo({ mode: 'global' })
    }
  }

  const isItineraryRunning = React.useMemo(() => itineraryStatus === 'ready' || itineraryStatus === 'loading', [
    itineraryStatus,
  ])

  if (isLoading) {
    return <Loader type="Grid" color={theme.colors.blue} height={150} width={150} />
  }

  return (
    <MainContainer>
      <MapContainer ref={mapRef} tabIndex={-1} />
      <ContentContainer>
        <TotemBuildingSelector
          buildings={buildingsLocations}
          defaultBuilding={defaultPOIArea?.building}
          currentBuilding={currentBuilding}
          onBuildingSelected={(visioglobeBuildingId) => {
            setSelectedTab(undefined)
            visioglobeBuildingId && actions.goTo(visioglobeBuildingId)
          }}
        />
        {allFloors && (
          <FloorsScrollView>
            <FloorsButtonContainer>{allFloors.map(renderFloorButton)}</FloorsButtonContainer>
          </FloorsScrollView>
        )}
        <SearchContainer>
          {selectedTab === 'itinerary' ? (
            <TotemItinerary
              itinerary={itinerary}
              itineraryStatus={itineraryStatus}
              itineraryInfo={itineraryInfo}
              cancelItinerary={() => setSelectedTab('details')}
            />
          ) : (
            <>
              <ResultsContainer hidden={selectedTab !== 'search'}>
                <ResultsTitle>{i18n.t('screens.cartography.totem.searchInterestPoint')}</ResultsTitle>
                <ResultsFilterContainer>
                  {Object.values(RoomFilterType).map((filter, index) => (
                    <FilterButton
                      key={index}
                      selected={selectedFilter === filter}
                      onClick={() => {
                        resultsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
                        setSelectedFilter(filter as RoomFilterType)
                      }}>
                      {i18n.t(`screens.cartography.totem.filters.${filter}`)}
                    </FilterButton>
                  ))}
                </ResultsFilterContainer>
                {searchText.length === 0 && (
                  <ResultsSubtitle>{i18n.t('screens.cartography.totem.nearbyPoints')}</ResultsSubtitle>
                )}
                <POIResultsContainer ref={resultsContainerRef}>
                  {poiResults.length != 0 ? (
                    poiResults.map((poi) => <TotemPOIDetails onClick={onPOIClicked} key={poi.id} poi={poi} />)
                  ) : (
                    <ResultsSubtitle>{i18n.t('screens.cartography.totem.noResults')}</ResultsSubtitle>
                  )}
                </POIResultsContainer>
              </ResultsContainer>
              {selectedTab === 'details' && selectedPOI && (
                <POIDetailsContainer>
                  <TotemPOIDetails poi={selectedPOI} onClick={onPOIClicked} onItinerary={computeItinerary} />
                </POIDetailsContainer>
              )}

              <SearchBarContainer onClick={() => setSelectedTab('search')}>
                <SearchBar
                  ref={searchBarRef}
                  placeholder={i18n.t('screens.cartography.totem.searchPlaceHolder')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <ClearSearchButton
                  onClick={(e) => {
                    if (canClearInput) {
                      keyboardRef.current?.clearInput()
                      setSearchText('')
                      e.stopPropagation()
                      setSelectedTab(undefined)
                    }
                  }}>
                  <Icons
                    name={canClearInput ? 'cross' : 'magnifier'}
                    size={canClearInput ? 70 : 50}
                    color={theme.colors.blue}
                  />
                </ClearSearchButton>
              </SearchBarContainer>
              <TotemKeyboard
                onRef={(ref) => (keyboardRef.current = ref)}
                showKeyboard={selectedTab === 'search'}
                setShowKeyboard={(showKeyboard) => setSelectedTab(showKeyboard ? 'search' : undefined)}
                text={searchText}
                setText={setSearchText}
                textInputRef={searchBarRef}
              />
            </>
          )}
        </SearchContainer>
        <BackToDefaultButton onClick={onBottomRightButtonClick}>
          <Icons
            name={isItineraryRunning ? 'cross' : 'reset'}
            size={isItineraryRunning ? 60 : 40}
            color={theme.colors.blue}
          />
          <ButtonText>
            {i18n.t(`screens.cartography.totem.${isItineraryRunning ? 'stopItinerary' : 'backToDefaultLocation'}`)}
          </ButtonText>
        </BackToDefaultButton>
      </ContentContainer>
    </MainContainer>
  )
}

export default TotemMap

const MainContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  flex-direction: column;
`

const ErrorMessage = styled('p')`
  ${(props) => props.theme.fonts.bodyBold};
  color: ${(props) => props.theme.colors.red};
  text-align: center;
`

const MapContainer = styled('div')`
  flex: 1;
`

const ContentContainer = styled('div')`
  position: absolute;
  height: 90%;
  width: 95%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`

const SearchContainer = styled('div')`
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: end;
  height: 100%;
  width: 40%;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`

const ResultsContainer = styled('div')<{ hidden: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 95%;
  opacity: ${(props) => (props.hidden ? 0 : 1)};
  transition: height 0.2s ease-in-out, opacity 0.2s ease-in-out 0.1s, padding 0s ease-in-out 0.2s;
  height: ${(props) => (props.hidden ? '0' : '55%')};
  overflow: hidden;
  padding: ${(props) => (props.hidden ? '0' : '36px')};
  margin: 0 auto auto;
  border-radius: 40px;
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
`

const ResultsTitle = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  font-weight: 600;
  font-size: 50px;
  margin: 0;
`

const ResultsFilterContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
`

const FilterButton = styled('button')<{ selected: boolean }>`
  ${(props) => props.theme.fonts.totemBigLight};
  flex-grow: 1;
  font-weight: 400;
  font-size: 40px;
  padding: 20px;
  background-color: ${(props) => (props.selected ? props.theme.colors.blue : props.theme.colors.mediumLightGrey)};
  color: ${(props) => (props.selected ? props.theme.colors.white : props.theme.colors.darkGrey)};
  border: none;
  text-align: center;
  transition: all 0.1s ease-in-out;

  &:active {
    transform: scale(0.95);
  }
`

const ResultsSubtitle = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  font-size: 36px;
  margin: 0;
  color: ${(props) => props.theme.colors.darkGrey};
`

const POIResultsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  width: 100%;
  border-radius: 20px;
  gap: 30px;
`

const POIDetailsContainer = styled('div')`
  display: flex;
  flex: 1;
  max-height: 200px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  margin: 0 auto;
  border-radius: 20px;
  background-color: ${(props) => props.theme.colors.white};
  margin-bottom: 80px;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
`

const SearchBarContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 80%;
  height: 4%;
  align-items: center;
  padding: 25px 35px;
  margin: 0 auto;
  border-radius: 80px;
  border: none;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  background-color: ${(props) => props.theme.colors.white};
`

const SearchBar = styled('input')`
  ${(props) => props.theme.fonts.totemBigLight};
  flex: 1;
  padding: 0 20px;
  height: 100%;
  border-radius: 2.5rem;
  outline: none;
  border: none;
`

const ClearSearchButton = styled('button')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70px;
  width: 70px;
  transition: all 0.1s ease-in-out;

  &:active {
    transform: scale(0.8);
  }
`

const BackToDefaultButton = styled('button')`
  background-color: ${(props) => props.theme.colors.white};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 15px;
  position: absolute;
  bottom: 0;
  right: 0;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
  height: 90px;
  padding: 0 25px;

  &:active {
    transform: scale(0.95);
  }
`

const ButtonText = styled('p')`
  ${(props) => props.theme.fonts.totemBigLight};
  color: ${(props) => props.theme.colors.darkGrey};
`

const FloorsScrollView = styled('div')`
  position: absolute;
  bottom: 0;
  left: 0;
  max-height: 50%;
  overflow-y: auto;
  overflow-x: hidden;
  /* Masque pour le haut et le bas */
  mask-image: linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%),
    /* Dégradé pour le haut et le bas */ linear-gradient(to top, transparent 0%, black 5%, black 95%, transparent 100%); /* Dégradé complémentaire pour assurer le fondu */

  /* Positionnement des masques pour qu'ils se combinent */
  mask-composite: intersect; /* Combine les masques pour qu'ils s'appliquent ensemble */
  -webkit-mask-composite: source-in; /* Ancien équivalent pour WebKit (peut varier) */

  /* Pour les navigateurs WebKit (Chrome, Safari) */
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%),
    linear-gradient(to top, transparent 0%, black 5%, black 95%, transparent 100%);
`

const FloorsButtonContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 200px;
  padding: 40px 0;
`
